// SPEC-OFFLINE-CONTINUATION-NO-ANON-01 (D-304) IMPL_1: 圏外継続モードのチョークポイント守衛。
//   React にも supabase にも依存しない純粋モジュール(循環 import を避ける)。logger のみ利用。
//   supabase-js v2 の createClient 第3引数 global.fetch に createGuardedFetch を挿すと、
//   PostgREST/Storage/Functions/Auth の全サービスクライアントに配られる=1箇所で anon REST を構造的に止められる。
import { logger } from '../logger'

export const OFFLINE_ERROR_CODE = 'OFFLINE_CONTINUATION'

// 圏外継続の真偽。React の外(supabase.js)から参照するためモジュール変数で保持。真実は AuthProvider が立てる。
let _offline = false
// 止めたパスの警告 dedup(Sentry を埋めない。storeSync の _d231SyncSent と同じ Set 方式)。
const _warnedPaths = new Set()

// SPEC-OFFLINE-GUARD-DEADLOCK-01 (D-348) FIX_3: 継続中に通した読み取りが成功したら復帰を促すコールバック。
//   AuthProvider が revalidate を登録する。守衛(fetch)は読み取り 2xx を検知して1回だけ呼ぶ(新規ポーリングは足さない=AC7)。
//   フラグ _offline の真偽は AuthProvider が所有する(復帰 cb の結果=adoptValid / 確定無効で setOfflineContinuation(false))。
let _onRecover = null
// 同一継続窓での多重発火を防ぐ(連続読み取りで revalidate を連打しない)。setOfflineContinuation の状態変化でリセット。
let _recoverScheduled = false

export function setOfflineContinuation(v) {
  const next = !!v
  if (next !== _offline) _recoverScheduled = false // 継続の入り直し/解除で復帰スケジュールを再武装
  _offline = next
}
export function isOfflineContinuation() { return _offline }
// FIX_3: 復帰コールバックの登録。返り値で解除できる(AuthProvider の effect cleanup 用)。
export function registerOfflineRecovery(cb) {
  _onRecover = typeof cb === 'function' ? cb : null
  return () => { if (_onRecover === cb) _onRecover = null }
}
export function _resetOfflineGate() { _offline = false; _warnedPaths.clear(); _onRecover = null; _recoverScheduled = false }
// SPEC-OFFLINE-BADGE-FALSE-POSITIVE-01 (D-332): 守衛が実際に 503 で通信を止めた実績があるかの読み取り専用アクセサ。
//   バッジ表示の信頼度向上に使う「通信が本当に届いていない兆候」。createGuardedFetch の遮断対象・判定式には一切触れない。
export function offlineGuardHasBlocked() { return _warnedPaths.size > 0 }

function urlOf(input) {
  if (typeof input === 'string') return input
  if (input && typeof input.url === 'string') return input.url // Request オブジェクト
  try { return String(input ?? '') } catch { return '' }
}
function pathOf(url) {
  try { return new URL(url).pathname } catch { return url }
}
function warnOnce(url) {
  const p = pathOf(url)
  if (_warnedPaths.has(p)) return
  _warnedPaths.add(p)
  logger.warn?.('OFFLINE_CONTINUATION_BLOCK', { path: p })
}

// SPEC-OFFLINE-GUARD-DEADLOCK-01 (D-348) FIX_2: init(または Request オブジェクト)から HTTP メソッドを取り出す。
//   postgrest-js/storage-js/functions-js は fetch に method を必ず付与する(調査で実測)。未指定は GET とみなす(読み取り=素通し側)。
function methodOf(input, init) {
  const m = (init && init.method) || (input && typeof input === 'object' && input.method) || 'GET'
  return String(m).toUpperCase()
}
const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

// FIX_3: 継続中に通した読み取りが 2xx 成功した実績を記録し、復帰 cb を1回だけ呼ぶ。
//   通信が本当に戻ったことの唯一の確証(getSession はローカル読取で通信しないことがある=デッドロックの原因)。
function noteReadSuccess(res) {
  if (!_offline || _recoverScheduled) return res
  if (res && res.ok) {
    _recoverScheduled = true
    try { _onRecover?.() } catch { /* 復帰 cb の失敗は通信を止めない */ }
  }
  return res
}

// baseFetch を包んだ fetch を返す。継続中でないときは素通し(引数を透過)。
export function createGuardedFetch(baseFetch) {
  return function guardedFetch(input, init) {
    if (!_offline) return baseFetch(input, init)
    const url = urlOf(input)
    // 通す: /auth/v1/(セッション復帰と health)。ここを塞ぐと継続から永久に出られない。
    if (url.includes('/auth/v1/')) return baseFetch(input, init)
    const isSupabase = url.includes('/rest/v1/') || url.includes('/storage/v1/') || url.includes('/functions/v1/')
    // 判定不能な URL は通す(誤遮断の害の方が大きい)。
    if (!isSupabase) return baseFetch(input, init)
    // FIX_2: 止めるのは書き込み動詞のみ。読み取り(GET/HEAD/未指定)は投げる=まず試して、失敗したら積む(オフラインファースト標準)。
    //   これで pre-auth のスタッフリスト読み取りや復帰検知のための読み取りが詰まらない。anon 書き込みは従来どおり止める(AC6)。
    if (!WRITE_METHODS.has(methodOf(input, init))) {
      // 読み取りは素通し。成功したら FIX_3 の復帰を促す(継続中のみ)。
      return baseFetch(input, init).then(noteReadSuccess)
    }
    warnOnce(url)
    // throw ではなく 503 合成レスポンス。supabase-js が非2xx を error に変換し、既存の error 分岐がそのまま働く。
    const body = JSON.stringify({
      message: '圏外継続中のため通信しません',
      code: OFFLINE_ERROR_CODE,
      details: '端末内に保存し、オンライン復帰後に自動送信します',
      hint: null,
    })
    return Promise.resolve(new Response(body, { status: 503, headers: { 'Content-Type': 'application/json' } }))
  }
}
